// src/pages/CheckoutPage.jsx
import { useAuth0 } from "@auth0/auth0-react";
import CheckoutSummary from "components/checkout/CheckoutSummary";
import PropTypes from "prop-types";
import { useEffect, useState, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import {
  handleTaxShippingEstimate,
  calculateOrderTotal,
  getCurrentPrice,
} from "config/config";
import useCartDetails from "hooks/useCartDetails";

import { MultiStepIndicator, Loading, EmptyCart } from "@/common";
import Breadcrumb from "@/common/navigation/Breadcrumb";
import ToastNotification from "@/common/toast/Toast";
import { CheckoutPayment, CheckoutReview } from "@/components/checkout";
import { Icon } from "@/design-system";
import useInitialAddress from "@/hooks/useInitialAddress";
import { selectCartSubtotalWithDiscounts } from "@/redux/slices";

export default function CheckoutPage({ type = "checkout" }) {
  const { getAccessTokenSilently } = useAuth0();
  const location = useLocation(); // kept (used for step detection)

  // Tax and shipping estimation state
  const [estimatedTax, setEstimatedTax] = useState(null);
  const [shippingCost, setShippingCost] = useState(null);
  const [taxRate, setTaxRate] = useState(null);
  const [stripeCustomerId, setStripeCustomerId] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [orderNote, setOrderNote] = useState("");
  const { cartDetails, cartDetailsLoading } = useCartDetails();

  const userInfo = useSelector((state) => state.user.info);
  const cartItems = useSelector((state) => state.cart.items);

  // ✅ Only show selected items; if none selected, show all
  const effectiveCart = useMemo(() => {
    const selected = cartDetails.filter((i) => i.selected);
    return selected.length ? selected : cartDetails;
  }, [cartDetails]);

  // Subtotal using the effective cart
  const subtotal = useSelector((state) =>
    selectCartSubtotalWithDiscounts(state, effectiveCart)
  );

  // The same per-line prices the subtotal selector sums, kept as lines so the
  // tax estimate can round per line exactly as NetSuite does (a one-cent
  // overshoot fails the Stripe capture — see estimateTaxPerLine).
  const taxLines = useMemo(
    () =>
      effectiveCart.map((item) => ({
        unitPrice: getCurrentPrice(item, item.quantity).currentPrice,
        quantity: item.quantity,
      })),
    [effectiveCart]
  );

  // Calculate order total centrally
  const { total } = calculateOrderTotal(subtotal, shippingCost, estimatedTax);

  const {
    shippingAddresses,
    setShippingAddresses,
    selectedShippingId,
    setSelectedShippingId,
    billingAddresses,
    setBillingAddresses,
    selectedBillingId,
    setSelectedBillingId,
  } = useInitialAddress(userInfo);

  const estimateTaxAndShipping = useCallback(
    async (userCountry, userPostalCode, userState) => {
      if (!userCountry || !userPostalCode || !subtotal) return;

      let toastId;

      const result = await handleTaxShippingEstimate({
        country: userCountry.toLowerCase(),
        postalCode: userPostalCode,
        state: userState,
        subtotal,
        lines: taxLines,
        userCategory: userInfo?.category,
        onSuccess: (data) => {
          setEstimatedTax(data.estimatedTax);
          setShippingCost(data.shippingCost);
          setTaxRate(data.taxRate);
        },
        onError: (errorMessage) => {
          ToastNotification.error(errorMessage);
        },
        onLoading: (message) => {
          toastId = ToastNotification.loading(message);
        },
        onDismiss: () => {
          if (toastId) ToastNotification.dismiss(toastId);
        },
      });

      if (!result.success) {
        // Reset states on error
        setEstimatedTax(null);
        setShippingCost(null);
        setTaxRate(null);
      }
    },
    [subtotal, taxLines, userInfo?.category]
  );

  useEffect(() => {
    (async () => {
      try {
        await getAccessTokenSilently({
          audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        });
      } catch (err) {
        // Error getting access token
      }
    })();
  }, [getAccessTokenSilently]);

  // Auto-estimate tax and shipping when selected shipping address is available
  useEffect(() => {
    if (selectedShippingId && shippingAddresses.length > 0 && subtotal > 0) {
      const selectedAddress = shippingAddresses.find(
        (addr) => addr.id === selectedShippingId
      );

      if (selectedAddress) {
        const userCountry = selectedAddress.country;
        const userPostalCode = selectedAddress.zip;
        const userState = selectedAddress.state;

        if (userCountry && userPostalCode) {
          estimateTaxAndShipping(userCountry, userPostalCode, userState);
        }
      }
    }
  }, [selectedShippingId, shippingAddresses, subtotal, estimateTaxAndShipping]);

  const renderStep = () => {
    const step = location.pathname.split("/")[2] || "payment";

    switch (step) {
      case "payment":
        return (
          <CheckoutPayment
            shippingAddresses={shippingAddresses}
            setShippingAddresses={setShippingAddresses}
            selectedShippingId={selectedShippingId}
            setSelectedShippingId={setSelectedShippingId}
            billingAddresses={billingAddresses}
            setBillingAddresses={setBillingAddresses}
            selectedBillingId={selectedBillingId}
            setSelectedBillingId={setSelectedBillingId}
            onCustomerIdChange={setStripeCustomerId}
            orderNote={orderNote}
            setOrderNote={setOrderNote}
          />
        );
      case "review":
        return (
          <CheckoutReview
            stripeCustomerId={stripeCustomerId}
            orderTotal={total}
            isProcessing={isProcessing}
            setIsProcessing={setIsProcessing}
            orderNote={orderNote}
            cart={effectiveCart}
          />
        );
      default:
        return null;
    }
  };

  const steps = [
    {
      label: "Payment & Shipping",
      description:
        type === "re-order"
          ? "Select your payment method and shipping address for re-order"
          : "Select your payment method and shipping address",
    },
    {
      label: "Review & Complete",
      description:
        type === "re-order"
          ? "Review your re-order details and complete your purchase"
          : "Review your order details and complete your purchase",
    },
  ];

  const getCurrentStep = () => {
    const step = location.pathname.split("/")[2] || "payment";
    return step === "review" ? 1 : 0;
  };

  const getCompletedStep = () => {
    const step = location.pathname.split("/")[2] || "payment";
    return step === "review" ? 0 : -1;
  };

  // If the real cart is empty, show empty state
  if (!cartItems || cartItems.length === 0) {
    return <EmptyCart />;
  }

  // Show loading while fetching cart details
  if (cartDetailsLoading) {
    return <Loading text="Loading cart details..." />;
  }

  return (
    // The reference CHECKOUT shell (grep `===== CHECKOUT` in the `.dc.html`):
    // container-lg, a "Signed in as" verified pill, then the 1.6fr/1fr grid with
    // the sticky "Your order" rail. The two-step wizard inside the left cell is
    // the app's own (real address book + saved cards need the extra step), so the
    // step indicator stays.
    <div className="relative mx-auto max-w-[var(--container-lg)] px-[var(--gutter)] pb-[var(--space-20)] pt-[var(--space-7)]">
      {/* Breadcrumbs */}
      <Breadcrumb
        path={["Home", "Cart", type === "re-order" ? "Re-order" : "Checkout"]}
      />

      <div className="mb-[var(--space-4)] mt-[var(--space-4)] flex flex-wrap items-center justify-between gap-[var(--space-4)]">
        <h1 className="font-sans text-3xl font-semibold leading-[1.1] text-ink">
          {type === "re-order" ? "Re-order items" : "Checkout"}
        </h1>
        {(userInfo?.companyname || userInfo?.firstname) && (
          <div className="inline-flex items-center gap-[var(--space-2)] rounded-pill border border-blue-100 bg-soft px-[14px] py-[var(--space-2)] text-sm text-blue-900">
            <Icon
              name="verified_user"
              size={18}
              fill
              className="text-blue-600"
            />
            Signed in as <b>{userInfo.companyname || userInfo.firstname}</b>
          </div>
        )}
      </div>

      {/* Step navigation */}
      <MultiStepIndicator
        steps={steps}
        currentStep={getCurrentStep()}
        completedStep={getCompletedStep()}
      />

      {/* Page layout */}
      <div className="grid grid-cols-1 items-start gap-[var(--space-8)] lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
        <div className="min-w-0">{renderStep()}</div>
        <CheckoutSummary
          shippingCost={shippingCost}
          estimatedTax={estimatedTax}
          taxRate={taxRate}
          calculatedTotal={total}
          subtotal={subtotal}
          cart={effectiveCart} // ← only selected items appear
        />
      </div>
    </div>
  );
}

// PropTypes definition
CheckoutPage.propTypes = {
  type: PropTypes.oneOf(["checkout", "re-order"]),
};

CheckoutPage.defaultProps = {
  type: "checkout",
};
